/**
 * ClearPass.ts — clears the shared HDR RenderTarget at the start of the frame.
 *
 * Doubles as a sanity check before SkyPass exists: its clear color shows up on
 * canvas after BlitPass tonemapping. Stage-1 SkyPass will overwrite the color
 * via a fullscreen draw, so this pass simply guarantees defined contents and
 * resets the reverse-Z depth buffer to 0.0.
 */

import {
  REVERSE_Z_CLEAR_DEPTH,
  type ManagedRenderTarget,
} from "./RenderTarget";
import type { FrameContext, RenderPass } from "./RenderPass";

export class ClearPass implements RenderPass {
  readonly name = "ClearPass";

  constructor(
    private rt: ManagedRenderTarget,
    /** Linear RGB clear color. Stage 1 default: a noticeable magenta until SkyPass exists. */
    public clearColor: [number, number, number] = [0.6, 0.0, 0.6],
  ) {}

  encode(encoder: GPUCommandEncoder, _frame: FrameContext): void {
    const rt = this.rt.current;
    const pass = encoder.beginRenderPass({
      label: "ClearPass",
      colorAttachments: [
        {
          view: rt.colorView,
          clearValue: {
            r: this.clearColor[0],
            g: this.clearColor[1],
            b: this.clearColor[2],
            a: 1,
          },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: rt.depthView,
        depthClearValue: REVERSE_Z_CLEAR_DEPTH,
        depthLoadOp: "clear",
        depthStoreOp: "store",
      },
    });
    pass.end();
  }
}
